import React from 'react';
import { GoogleMap, MarkerF, MarkerClustererF } from '@react-google-maps/api';
import { Image, DatePicker, Spin, Select } from 'antd';
import { MapPin, Store, Building2 } from 'lucide-react';
import { FilterBar, FilterField, StatusTag, EmptyState } from '../../common';
import { useStoresMap } from './storesMap.controller';
import { BASE_MAP_OPTIONS, CLUSTER_STYLES, DEFAULT_MAP_CENTER, PIN_TONES, fitMapToPoints, pinIcon, useShelfMapsLoader } from '../../common/map/mapTheme';

const { RangePicker } = DatePicker;

const MAP_LEGEND = [
    { color: 'success', pin: PIN_TONES.success, label: 'Con storecheck' },
    { color: 'danger', pin: PIN_TONES.danger, label: 'Sin storecheck' }
];

const toPoint = (store) => ({ lat: store?.coordinates?.latitude, lng: store?.coordinates?.longitude });

/** Selected marker: photo, storecheck status, store and client. */
function SelectedStorePanel({ store, visited, showRecency = true }) {
    return (
        <aside className="flex flex-col gap-3 w-full lg:w-[360px] shrink-0">
            <Image
                className="object-cover rounded-lg"
                rootClassName="block w-full overflow-hidden rounded-lg border border-surface-200 bg-surface-50"
                width="100%"
                height={220}
                src={store?.image}
                alt=""
                fallback="/not-found.png"
            />
            {showRecency && (
                <StatusTag tone={visited ? 'success' : 'danger'} dot className="w-fit">
                    {visited ? 'Storecheck en el rango seleccionado' : 'Sin storecheck en el rango seleccionado'}
                </StatusTag>
            )}
            <div className="flex flex-col gap-2 rounded-lg border border-surface-200 p-3">
                <div className="flex items-start gap-2 text-sm">
                    <Store className="mt-0.5 text-surface-400" />
                    <span className="text-surface-500 shrink-0">PDV:</span>
                    <span className="font-medium text-surface-900">{store?.name}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                    <Building2 className="mt-0.5 text-surface-400" />
                    <span className="text-surface-500 shrink-0">Cliente:</span>
                    <span className="font-medium text-surface-900">{store?.clientName}</span>
                </div>
            </div>
        </aside>
    );
}

/**
 * Stores on a map. `showRecency` (default on) colours markers by whether the store had a store check in
 * the picked range; off for backends without that data, which show plain markers and no range filter.
 */
function StoreMap({ stores, clients, visitedStoreIds = [], onDateRangeChange, mapDateRange, mapRecencyLoading = false, showRecency = true }) {
    const {
        selectedStore,
        filteredStores,
        channel,
        setChannel,
        setClient,
        setStoreType,
        handleMarkerClick,
        storeTypes,
        isVisited,
        greenCount,
        redCount
    } = useStoresMap({ stores, visitedStoreIds });
    const { isLoaded, loadError } = useShelfMapsLoader();
    const [map, setMap] = React.useState(null);

    // Fit the view to whatever the filters show.
    React.useEffect(() => {
        fitMapToPoints(map, filteredStores.map(toPoint));
    }, [map, filteredStores]);

    const pinFor = (store) => {
        const selected = selectedStore?._id === store._id;
        if (!showRecency) return pinIcon(PIN_TONES.neutral, selected);
        return pinIcon(isVisited(store) ? PIN_TONES.success : PIN_TONES.danger, selected);
    };

    if (stores?.length === 0) {
        return (
            <div className="shelf-card">
                <EmptyState icon={<MapPin />} title="Sin pisos de venta" description="No hay pisos de venta con coordenadas" />
            </div>
        );
    }

    return (
        <div>
            <FilterBar>
                <FilterField label="Canal" width={170}>
                    <Select
                        className="w-full"
                        defaultValue="both"
                        onChange={setChannel}
                        options={[
                            { value: 'both', label: 'Todos' },
                            { value: 'moderno', label: 'Moderno' },
                            { value: 'tradicional', label: 'Tradicional' }
                        ]}
                    />
                </FilterField>
                <FilterField label="Cliente" width={240}>
                    <Select
                        showSearch
                        className="w-full"
                        defaultValue="both"
                        onChange={setClient}
                        options={[{ value: 'both', label: 'Todos' }, ...clients?.map((c) => ({ value: c?.name, label: c?.name }))]}
                    />
                </FilterField>
                {channel === 'tradicional' && (
                    <FilterField label="Segmentación" width={220}>
                        <Select
                            showSearch
                            className="w-full"
                            defaultValue="both"
                            onChange={setStoreType}
                            options={[{ label: 'Todos', value: 'both' }, ...storeTypes]}
                        />
                    </FilterField>
                )}
                {showRecency && (
                    <FilterField label="Rango de auditorías" width={280}>
                        <RangePicker className="w-full" value={mapDateRange} onChange={(dates) => onDateRangeChange?.(dates)} format="DD/MM/YYYY" />
                    </FilterField>
                )}
            </FilterBar>

            <section className="shelf-card">
                {/* Legend */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3 border-b border-surface-100">
                    <span className="text-[13px] font-semibold text-surface-900">Leyenda</span>
                    {(showRecency ? MAP_LEGEND : []).map(({ color, pin, label }) => (
                        <span key={color} className="inline-flex items-center gap-2">
                            <span className="inline-block h-2.5 w-2.5 rounded-full ring-2 ring-white shadow-sm" style={{ background: pin }} />
                            <StatusTag tone={color}>{label}</StatusTag>
                        </span>
                    ))}
                    <span className="ml-auto text-xs text-surface-500 tabular-nums">
                        {!showRecency ? (
                            <>{filteredStores.length} en mapa</>
                        ) : mapRecencyLoading ? (
                            <Spin size="small" />
                        ) : (
                            <>
                                {greenCount} con storecheck · {redCount} sin storecheck · {filteredStores.length} en mapa
                            </>
                        )}
                    </span>
                </div>

                <div className="flex flex-col lg:flex-row gap-5 p-5">
                    <div className="flex-1 min-w-0 overflow-hidden rounded-lg border border-surface-200">
                        {loadError ? (
                            <div className="flex h-[520px] items-center justify-center bg-surface-50">
                                <EmptyState icon={<MapPin />} title="No se pudo cargar el mapa" description="Revisa tu conexión e intenta de nuevo." />
                            </div>
                        ) : !isLoaded ? (
                            <div className="flex h-[520px] items-center justify-center bg-surface-50">
                                <Spin />
                            </div>
                        ) : (
                            <GoogleMap
                                center={DEFAULT_MAP_CENTER}
                                zoom={10}
                                options={BASE_MAP_OPTIONS}
                                onLoad={setMap}
                                onUnmount={() => setMap(null)}
                                mapContainerStyle={{ height: '520px', width: '100%' }}>
                                <MarkerClustererF styles={CLUSTER_STYLES} averageCenter gridSize={48} maxZoom={14}>
                                    {(clusterer) =>
                                        filteredStores?.map((store) => (
                                            <MarkerF
                                                key={store._id}
                                                clusterer={clusterer}
                                                icon={pinFor(store)}
                                                title={store?.name}
                                                zIndex={selectedStore?._id === store._id ? 1000 : undefined}
                                                onClick={() => {
                                                    handleMarkerClick(store);
                                                    map?.panTo(toPoint(store));
                                                }}
                                                position={toPoint(store)}
                                            />
                                        ))
                                    }
                                </MarkerClustererF>
                            </GoogleMap>
                        )}
                    </div>
                    {selectedStore?._id ? (
                        <SelectedStorePanel store={selectedStore} visited={isVisited(selectedStore)} showRecency={showRecency} />
                    ) : (
                        <aside className="hidden lg:flex w-[360px] shrink-0 rounded-lg border border-dashed border-surface-200">
                            <EmptyState
                                className="w-full"
                                icon={<MapPin />}
                                title="Selecciona un PDV"
                                description="Haz clic en un marcador del mapa para ver su foto y detalle."
                            />
                        </aside>
                    )}
                </div>
            </section>
        </div>
    );
}

export default StoreMap;
